import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { api, type SessionToolCall } from '../api/client';
import { PipelineStrip } from '../components/PipelineStrip';
import {
  CaretRight,
  TreeStructure,
  Clock,
  User,
} from '@phosphor-icons/react';
import {
  buildStepsFromEvents,
  deriveOverallStatus,
  formatToolName,
  getStatusStyle,
} from '../utils/trace-utils';

export function SessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { currentWorkspaceId: workspaceId } = useAuth();
  const navigate = useNavigate();

  const [toolCalls, setToolCalls] = useState<SessionToolCall[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId || !sessionId) return;
    setLoading(true);
    setError(null);
    api
      .getSessionDetail(workspaceId, sessionId)
      .then((data) => {
        setToolCalls(data.tool_calls);
        setTotalEvents(data.total_events);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load session.'),
      )
      .finally(() => setLoading(false));
  }, [workspaceId, sessionId]);

  const totalDuration = useMemo(
    () => toolCalls.reduce((sum, tc) => sum + (tc.duration_ms || 0), 0),
    [toolCalls],
  );

  const overallStatus = useMemo(() => {
    if (toolCalls.some((tc) => tc.status === 'blocked')) return 'blocked';
    if (toolCalls.some((tc) => tc.status === 'error')) return 'error';
    if (toolCalls.every((tc) => tc.status === 'allowed')) return 'allowed';
    return 'mixed';
  }, [toolCalls]);

  const statusStyle = useMemo(() => getStatusStyle(overallStatus), [overallStatus]);

  const actorIds = useMemo(() => {
    const ids = new Set(
      toolCalls.flatMap((tc) => tc.events.map((e) => e.actor_id).filter(Boolean)),
    );
    return [...ids];
  }, [toolCalls]);

  if (!workspaceId) {
    return (
      <div className="card p-8 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
        No workspace found. Please complete onboarding first.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm">
        <Link
          to="/traces"
          className="transition-colors hover:underline"
          style={{ color: 'var(--accent-teal)' }}
        >
          Traces
        </Link>
        <CaretRight size={12} weight="bold" style={{ color: 'var(--text-tertiary)' }} />
        <span className="font-mono" style={{ color: 'var(--text-secondary)' }}>
          {sessionId}
        </span>
      </nav>

      {/* Error */}
      {error && (
        <div
          className="rounded-lg px-4 py-3 text-sm"
          style={{
            background: 'var(--color-error-muted)',
            border: '1px solid rgba(248,113,113,0.25)',
            color: 'var(--color-error)',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="spinner-gradient h-8 w-8" />
        </div>
      ) : toolCalls.length === 0 ? (
        <div className="card p-12 text-center">
          <TreeStructure
            size={40}
            weight="duotone"
            className="mx-auto mb-3"
            style={{ color: 'var(--text-tertiary)' }}
          />
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            No tool calls found
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            No tool calls were recorded for this session.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Header Card */}
          <div className="card p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Session: {toolCalls.length} tool call{toolCalls.length !== 1 ? 's' : ''}
                </h1>
                <p className="text-sm font-mono" style={{ color: 'var(--text-tertiary)' }}>
                  {sessionId}
                </p>
                {actorIds.length > 0 && (
                  <div className="flex items-center gap-1 pt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    <User size={12} weight="duotone" />
                    {actorIds.join(', ')}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <Clock size={14} weight="duotone" />
                  <span>{totalDuration}ms total</span>
                </div>
                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  {totalEvents} events
                </span>
                <span
                  className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
                  style={{ background: statusStyle.bg, color: statusStyle.text }}
                >
                  {statusStyle.label}
                </span>
              </div>
            </div>
          </div>

          {/* Tool Calls List */}
          <div className="card overflow-hidden">
            <div
              className="flex items-center px-5 py-3 text-xs font-medium"
              style={{
                borderBottom: '1px solid var(--border)',
                color: 'var(--text-tertiary)',
              }}
            >
              <div className="w-64 shrink-0">Tool Call</div>
              <div className="flex-1">Pipeline</div>
              <div className="w-20 shrink-0 text-right">Duration</div>
              <div className="w-24 shrink-0 text-right">Status</div>
            </div>

            {toolCalls.map((tc) => {
              const steps = buildStepsFromEvents(tc.events);
              const status = deriveOverallStatus(tc.events);
              const sStyle = getStatusStyle(status);
              const name = formatToolName(tc.tool_name, tc.events);

              return (
                <button
                  key={tc.tool_call_id}
                  onClick={() => navigate(`/traces/${tc.task_id}`)}
                  className="flex w-full items-center px-5 py-4 text-left transition-colors"
                  style={{ borderBottom: '1px solid var(--border-subtle)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {/* Tool name */}
                  <div className="w-64 shrink-0 space-y-0.5">
                    <span className="text-sm font-medium truncate block" style={{ color: 'var(--text-primary)' }}>
                      {name}
                    </span>
                    <span className="text-xs font-mono truncate block" style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>
                      {tc.tool_call_id.slice(0, 16)}...
                    </span>
                  </div>

                  {/* Pipeline strip */}
                  <div className="flex-1 px-2">
                    <PipelineStrip steps={steps} size="sm" />
                  </div>

                  {/* Duration */}
                  <div className="w-20 shrink-0 text-right text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                    {tc.duration_ms}ms
                  </div>

                  {/* Status */}
                  <div className="w-24 shrink-0 flex justify-end">
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                      style={{ background: sStyle.bg, color: sStyle.text }}
                    >
                      {sStyle.label}
                    </span>
                  </div>

                  <CaretRight
                    size={12}
                    weight="bold"
                    className="ml-2 shrink-0"
                    style={{ color: 'var(--text-tertiary)' }}
                  />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
